/*
 * IcLagerKorrekturbuchung.tsx
 *
 * Created by Dr. Maximillian Dornseif 2022-12-28 in huWaWi3 31.12.0
 * Copyright (c) 2022 HUDORA GmbH
 */

import { useTheme } from '@fluentui/react'
import Form from '@hudora/hd-rjsf'
import { assertIsArray, assertIsObject } from 'assertate-debug'
import { useTitle } from 'fluentui-hooks'
import gql from 'graphql-tag'
import { JSONSchema7 } from 'json-schema'
import React, { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import ReactJson from 'react-json-view'
import { useMutation, useQuery } from 'urql'
import { navigate } from 'wouter/use-location'
import * as z from 'zod'

import { LAGER_FRAGMENT } from '../fragments'
import { KindListe } from '../lib/components/KindListe'
import { SKU_BESTAND_LIST_QUERY } from './IcSkusApp'

const DEFAULT_SCHEMA = {
  $id: 'http://huwawi3.hudora.de/schemata/GuiKorrekturbuchung',
  $schema: 'http://json-schema.org/draft-07/schema',
  title: 'Korrekturbuchung',
  description: 'Eine manuelle Lager-Buchung auf eine SKU.',
  required: ['menge', 'sku', 'lager', 'bewegungsart', 'belegnr'],
  type: 'object',
  properties: {
    bewegungsart: {
      title: 'Art',
      description: 'Um Welche Sorte Bewegung handelt es sich?',
      type: 'string',
      default: 'abgang',
      enum: ['bestellung', 'produktion', 'umlagerung', 'zugang', 'abgang'],
      enumNames: [
        'Zugang aus Bestellung',
        'Zugang aus Produktion',
        'Zugang aus Umlagerung',
        'anderer Warenzugang - nacher ist mehr am Lager als vorher',
        'anderer Warenabgang - nachher ist weniger im Lager als vorher',
      ],
      maxLength: 35,
    },
    menge: {
      title: 'Menge',
      description: 'Positiv für Zugänge, negativ für Abgänge.',
      default: -1,
      examples: [5760, -1],
      type: 'integer',
    },
    sku: {
      title: 'SKU',
      description: 'SKU, auf die sich die Bewegung bezieht.',
      minLength: 5,
      maxLength: 8,
      examples: ['14795x01', '65123x01'],
      type: 'string',
    },
    belegnr: {
      title: 'Belegnummer',
      description: 'Grund für diese Buchung. Muss eindeutig sein. Zb Nummer im Korrektur-Protokoll',
      type: 'string',
      maxLength: 35,
    },
    lager: {
      title: 'Lager',
      description: 'Lager aus oder zu dem gebucht wird',
      default: 'LG200',
      type: 'string',
      enum: ['LG200'],
      enumNames: ['LG200 - Haupt-Lager'],
      maxLength: 5,
      minLength: 3,
    },
    vorgang: {
      title: 'Vorgang',
      description: 'PO-/Umlagerungs- oder Gutschriftennummer',
      type: 'string',
      maxLength: 35,
    },
    wert: {
      title: 'Wert in Cent',
      description:
        'Wert der gesamten Bewegung in Eurocent. Nicht pro Stück/Paar! Bei Abgängen freilassen, um automatisch zu berechnen.',
      type: 'integer',
      min: -100000,
      max: 100000,
    },
    info: {
      title: 'Info',
      description: 'Woher kommt die Buchung? Wie berechnet sich der Wert? usw.',
      type: 'string',
      maxLength: 100,
    },
  },
}

const zodSchema = z.object({
  menge: z.number().multipleOf(1),
  sku: z.string().trim(),
  belegnr: z.string().trim(),
  bewegungsart: z.string().trim(),
  lager: z.string(),
  vorgang: z.string().trim().optional(),
  wert: z.number().multipleOf(1).optional(),
  info: z.string().trim().optional(),
  test: z.boolean(),
})

export const IcLagerKorrekturbuchung = (props) => {
  useTitle('Korrekturbuchung Erfassen')
  const [schema, setSchema] = useState(DEFAULT_SCHEMA)
  const theme = useTheme()
  // Liste der aktiven Läger laden
  const [{ data, fetching }] = useQuery({
    query: LAGER_LIST_QUERY,
  })
  useEffect(() => {
    if (!fetching && data) {
      const laeger = []
      const lagerNames = []

      for (const l of data.nodes.edges) {
        laeger.push(l.node.designator)
        lagerNames.push(`${l.node.designator} - ${l.node.name}`)
      }
      setSchema((s: any) => {
        // Vorbelegung der SKU
        s.properties.lager.default = props.nr

        if (s.properties) {
          assertIsObject(s.properties)
          assertIsObject(s.properties.lager)
          assertIsArray(s.properties.lager.enum)
          s.properties.lager.enum = laeger
          assertIsArray(s.properties.lager.enumNames)
          s.properties.lager.enumNames = lagerNames
        }
        return { ...s }
      })
    }
  }, [data, setSchema])

  const [currentFormData, setCurrentFormData] = useState<any>({})
  const [, mutateEntity] = useMutation(gql`
    mutation createBuchung(
      $menge: Int!
      $sku: String!
      $belegnr: String!
      $lager: String!
      $vorgang: String
      $wert: Int
      $info: String
    ) {
      hwIcBestandsbuchEintragCreate(
        input: {
          artnr: $sku
          #  datum: "",
          lager: $lager
          menge: $menge
          beleg: $belegnr
          info: $info
          wert: $wert
          vorgang_posnr: -1
          vorgang: $vorgang
        }
      ) {
        _keyStr
        id
        designator
      }
    }
  `)

  // Daten per GraphQL Mutation an den Server senden.
  const uebertragen = useCallback(
    async (formData: any) => {
      if (!formData.info || formData.info === '') {
        formData.info = formData.bewegungsart
      }
      formData.bewegungsart = undefined
      const mutResult = await mutateEntity(formData as any)
      if (mutResult.error) {
        toast.error(`Fehler: ${mutResult.error.message}`)
      } else {
        toast.success(
          <span>
            Erfolgreich Buchung{' '}
            <a href={`/ic/bestandsbuch/${mutResult.data.hwIcBestandsbuchEintragCreate.designator}/`}>
              {mutResult.data.hwIcBestandsbuchEintragCreate.designator}
            </a>{' '}
            erstellt!
          </span>
        )
        // navigate(`/ic/lager/${formData.lager}/bestandsbuch`)
      }
    },
    [navigate]
  )

  const validate = useCallback(
    (formData, errors) => {
      const zodResult = zodSchema.safeParse(formData)
      if (!zodResult.success) {
        for (const err of (zodResult as any).error.issues) {
          errors[err.path[0]]?.addError(err.message)
          // code
          // "invalid_type"
          // expected
          // "string"
          // message
          // "Required"
          // received
          // "undefined
        }
      }
      if (
        ['bestellung', 'produktion', 'umlagerung'].includes(formData.bewegungsart) &&
        (formData.vorgang?.length === 0 || !formData.vorgang)
      ) {
        errors.vorgang.addError(`"${formData.bewegungsart}" erfordert eine Vorgangsnummer.`)
      }
      if (
        ['bestellung', 'produktion'].includes(formData.bewegungsart) &&
        (formData.wert === 0 || !Number.isInteger(formData.wert))
      ) {
        errors.wert.addError(`"${formData.bewegungsart}" erfordert einen Wert.`)
      }
      // Bei Zugängen wird eine positive Menge erwartet, bei Abgängen eine negative
      const expect_positive = formData.bewegungsart !== 'abgang'
      if (formData.menge < 0 && expect_positive) {
        errors.menge.addError(`Bei Zugängen muss Menge positiv sein`)
      }
      if (formData.menge > 0 && !expect_positive) {
        errors.menge.addError(`Bei Abgängen muss Menge negativ sein`)
      }
      if (formData.menge < 0 && formData.wert && formData.wert > 0) {
        errors.menge.addError(`Menge und Wert müssen das gleiche Vorzeichen haben.`)
      }
      if (formData.menge > 0 && formData.wert && formData.wert < 0) {
        errors.menge.addError(`Menge und Wert müssen das gleiche Vorzeichen haben.`)
      }

      return errors
    },
    [zodSchema]
  )
  console.log(schema)
  return (
    <section>
      <div>
        {schema ? (
          <Form
            schema={schema as JSONSchema7}
            formData={currentFormData}
            liveValidate
            customValidate={validate}
            onChange={({ formData }) => {
              setCurrentFormData(formData)
            }}
            onSubmit={({ formData }) => uebertragen(formData)}
          />
        ) : (
          <span>Lade Läger</span>
        )}
      </div>
      <hr />
      <div>
        {currentFormData.sku?.length > 4 ? (
          <KindListe
            className="authEnforced"
            schemaName={'HwIcBestandsbuchSumme'}
            listQuery={SKU_BESTAND_LIST_QUERY}
            title={`Bestände für ${currentFormData.sku}`}
            nr={currentFormData.sku}
            hideExcept={[
              'lager',
              'artnr',
              'lager_menge',
              'lager_wert',
              'adw',
              'zugangsmenge',
              'abgangsmenge',
              'bewegungen',
              'erster_zugang',
              'letzter_abgang',
              'gebucht_bis_ts',
              'gebucht_bis_datum',
            ]}
          />
        ) : (
          <div>SKU unvollständig</div>
        )}
      </div>
    </section>
  )
}

export const LAGER_LIST_QUERY = gql`
  query LagerPages {
    nodes: hwLagerPages(
      limit: 1000
      filter: { ist_aktiv: { eq: true } }
      sort: { fields: [ist_aktiv], order: DESC }
    ) {
      edges {
        cursor
        node {
          ...lagerFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${LAGER_FRAGMENT}
`
